import { MAX_PINNED_NOTES } from '@/constants'
import { currentPubkeyAtom } from './session'
import { getDefaultStore } from 'jotai'
import client from '@/services/client.service'
import * as kinds from '@nostr/tools/kinds'
import { NostrEvent } from '@nostr/tools/core'
import { toast } from 'sonner'
import { loadPins } from '@nostr/gadgets/lists'
import i18n from 'i18next'
import { invalidatePinList } from './invalidation'
import { now } from '@/lib/utils'

const store = getDefaultStore()
const t = i18n.t.bind(i18n)

export async function pin(event: NostrEvent): Promise<void> {
  const pubkey = store.get(currentPubkeyAtom)
  if (!pubkey) return

  if (event.kind !== kinds.ShortTextNote || event.pubkey !== pubkey) return

  const toastId = toast.loading(t('Pinning...'))
  try {
    const pins = await loadPins(pubkey)
    const currentTags = pins.event?.tags || []

    if (currentTags.some((tag) => tag[0] === 'e' && tag[1] === event.id)) {
      toast.dismiss(toastId)
      return
    }

    let newTags = [...currentTags, ['e', event.id, event.pubkey]]
    const eTagCount = newTags.filter((tag) => tag[0] === 'e').length
    if (eTagCount > MAX_PINNED_NOTES) {
      let removed = 0
      const needRemove = eTagCount - MAX_PINNED_NOTES
      newTags = newTags.filter((tag) => {
        if (tag[0] === 'e' && removed < needRemove) {
          removed += 1
          return false
        }
        return true
      })
    }

    const newPinListEvent = await client.publish({
      kind: kinds.Pinlist,
      content: pins.event?.content ?? '',
      tags: newTags,
      created_at: now()
    })
    if (!newPinListEvent) {
      console.warn('failed to publish updated pin event')
      toast.error(t('Failed to publish updated pin event.'), { id: toastId })
      return
    }

    loadPins(newPinListEvent.pubkey, undefined, newPinListEvent)
    invalidatePinList()
    toast.success(t('Pinned!'), { id: toastId })
  } catch (err) {
    console.warn('failed to pin:', (err as Error).message)
    toast.error(t('Failed to pin: {{error}}', { error: (err as Error).message }), { id: toastId })
  }
}

export async function unpin(event: NostrEvent): Promise<void> {
  const pubkey = store.get(currentPubkeyAtom)
  if (!pubkey) return
  if (event.kind !== kinds.ShortTextNote || event.pubkey !== pubkey) return

  const toastId = toast.loading(t('Unpinning...'))
  try {
    const pins = await loadPins(pubkey)
    if (!pins.event) {
      toast.dismiss(toastId)
      return
    }

    const newTags = pins.event.tags.filter((tag) => tag[0] !== 'e' || tag[1] !== event.id)
    if (newTags.length === pins.event.tags.length) {
      toast.dismiss(toastId)
      return
    }

    const newPinListEvent = await client.publish({
      kind: kinds.Pinlist,
      content: pins.event?.content ?? '',
      tags: newTags,
      created_at: now()
    })
    if (!newPinListEvent) {
      console.warn('failed to publish updated pin event')
      toast.error(t('Failed to publish updated pin event.'), { id: toastId })
      return
    }
    loadPins(newPinListEvent.pubkey, undefined, newPinListEvent)
    invalidatePinList()
    toast.success(t('Unpinned!'), { id: toastId })
  } catch (err) {
    console.warn('failed to unpin:', (err as Error).message)
    toast.error(t('Failed to unpin: {{error}}', { error: (err as Error).message }), { id: toastId })
  }
}
